import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useSiteBranding from '../hooks/useSiteBranding';
import usePublicContactDisplay from '../hooks/usePublicContactDisplay';
import { getTelHrefFromDisplayPhone } from '../utils/publicContactInfo';
import { CONTACT_CHANNELS_UPDATED_EVENT, getWhatsAppChatHelpUrl } from '../utils/whatsappContact';
import './Footer.css';

const Footer = () => {
  const { siteName, siteEmail: brandingEmail } = useSiteBranding();
  const { phoneDisplay, email: publicEmail, socialLinks } = usePublicContactDisplay();
  const { twitterUrl, facebookUrl, instagramUrl, tiktokUrl } = socialLinks || {};
  const hasSocial = !!(twitterUrl || facebookUrl || instagramUrl || tiktokUrl);
  const [email, setEmail] = useState('');
  const [waUrl, setWaUrl] = useState(() => getWhatsAppChatHelpUrl());
  const hasTrustSocial = !!(twitterUrl || facebookUrl || instagramUrl || tiktokUrl || waUrl);

  const displayEmail = brandingEmail || publicEmail;
  const telHref = getTelHrefFromDisplayPhone(phoneDisplay);

  useEffect(() => {
    const bump = () => setWaUrl(getWhatsAppChatHelpUrl());
    window.addEventListener(CONTACT_CHANNELS_UPDATED_EVENT, bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener(CONTACT_CHANNELS_UPDATED_EVENT, bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      alert(`Thank you for subscribing to ${siteName || 'our'} newsletter!`);
      setEmail('');
    }
  };

  return (
    <footer className="footer">
      <div className="footer-main">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Contact</h4>
              <div className="contact-info">
                <div className="contact-item">
                  <p>
                    <a href={telHref} className="footer-inline-contact">
                      <span aria-hidden>📞</span> {phoneDisplay}
                    </a>
                  </p>
                  <p>
                    <a href={`mailto:${displayEmail}`} className="footer-inline-contact">
                      <span aria-hidden>📧</span> {displayEmail}
                    </a>
                  </p>
                  <p className="footer-support-hours">
                    <strong>24/7</strong> — place orders anytime. Customer support open day &amp; night.
                  </p>
                </div>
              </div>
            </div>

            <div className="footer-section">
              <h4>Quick links</h4>
              <ul className="footer-links">
                <li>
                  <Link to="/about">About Us</Link>
                </li>
                <li>
                  <Link to="/contact">Contact Us</Link>
                </li>
                <li>
                  <Link to="/return-policy">Return &amp; Refund Policy</Link>
                </li>
                <li>
                  <Link to="/privacy-policy">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/payment-methods">Payment Methods</Link>
                </li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Follow Us</h4>
              {hasSocial ? (
                <div className="social-links">
                  {twitterUrl ? (
                    <a href={twitterUrl} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                      <i className="fab fa-x-twitter social-icon-fa" aria-hidden />
                    </a>
                  ) : null}
                  {facebookUrl ? (
                    <a href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                      <i className="fab fa-facebook-f social-icon-fa" aria-hidden />
                    </a>
                  ) : null}
                  {instagramUrl ? (
                    <a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                      <i className="fab fa-instagram social-icon-fa" aria-hidden />
                    </a>
                  ) : null}
                  {tiktokUrl ? (
                    <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                      <i className="fab fa-tiktok social-icon-fa" aria-hidden />
                    </a>
                  ) : null}
                </div>
              ) : null}

              <div className="newsletter-section">
                <h5>Stay Updated</h5>
                <p>Get exclusive offers &amp; latest collections</p>
                <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="newsletter-input"
                    required
                  />
                  <button type="submit" className="btn btn-primary newsletter-btn">
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-trust-row">
          <nav className="footer-trust-links" aria-label="Trust and policies">
            <Link className="footer-trust-link" to="/about">
              About Us
            </Link>
            <Link className="footer-trust-link" to="/contact">
              Contact Us
            </Link>
            <Link className="footer-trust-link" to="/return-policy">
              Return Policy
            </Link>
            <Link className="footer-trust-link" to="/privacy-policy">
              Privacy Policy
            </Link>
            <Link className="footer-trust-link" to="/payment-methods">
              Payment Methods
            </Link>
            {hasTrustSocial ? (
              <span className="footer-trust-social-group" role="group" aria-label="Social media and WhatsApp">
                {twitterUrl ? (
                  <a
                    href={twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-trust-social-btn footer-trust-social-btn--x"
                    aria-label="X (Twitter)"
                    title="X (Twitter)"
                  >
                    <i className="fab fa-x-twitter" aria-hidden />
                  </a>
                ) : null}
                {facebookUrl ? (
                  <a
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-trust-social-btn footer-trust-social-btn--facebook"
                    aria-label="Facebook"
                    title="Facebook"
                  >
                    <i className="fab fa-facebook-f" aria-hidden />
                  </a>
                ) : null}
                {instagramUrl ? (
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-trust-social-btn footer-trust-social-btn--instagram"
                    aria-label="Instagram"
                    title="Instagram"
                  >
                    <i className="fab fa-instagram" aria-hidden />
                  </a>
                ) : null}
                {tiktokUrl ? (
                  <a
                    href={tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-trust-social-btn footer-trust-social-btn--tiktok"
                    aria-label="TikTok"
                    title="TikTok"
                  >
                    <i className="fab fa-tiktok" aria-hidden />
                  </a>
                ) : null}
                {waUrl ? (
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-trust-social-btn footer-trust-social-btn--wa"
                    aria-label="WhatsApp"
                    title="Chat on WhatsApp"
                  >
                    <i className="fab fa-whatsapp" aria-hidden />
                  </a>
                ) : null}
              </span>
            ) : null}
          </nav>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            <div className="copyright">
              <p>&copy; {new Date().getFullYear()} {siteName || 'SUPER WEAR'}</p>
              <p>Premium fashion, trusted service.</p>
              <p className="footer-support-hours footer-support-hours--bottom">
                Orders &amp; support open <strong>24/7</strong>
              </p>
            </div>

            <div className="website-info">
              <p>
                <Link to="/" className="website-link">
                  🌐 Home
                </Link>
              </p>
              <p className="tagline">{siteName}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-pattern" />
    </footer>
  );
};

export default Footer;
